import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section
      id="home"
      className="relative overflow-hidden pt-28 pb-20 md:pt-32 md:pb-24"
    >
      {/* Fondo con tinte oliva + grises (más vida, sin perder sobriedad) */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#f6f7f4] via-white to-[#eef2ee]" />
      <div className="absolute -top-28 -left-28 h-96 w-96 rounded-full bg-[#4b5d3a]/14 blur-3xl" />
      <div className="absolute -bottom-32 -right-28 h-[28rem] w-[28rem] rounded-full bg-slate-400/14 blur-3xl" />

      {/* Línea sutil para “marca” */}
      <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-[#3f5232]/40 via-[#7b8f66]/30 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          {/* Texto */}
          <motion.div
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="md:col-span-7"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#3f5232]/15 bg-white/70 px-3 py-1 text-xs text-slate-700 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-[#3f5232]" />
              <span>{t("hero.kicker", "Frontend • Serverless • IA")}</span>
            </div>

            <h1 className="mt-4 font-poppins text-4xl md:text-5xl font-semibold tracking-tight text-slate-900">
              {t("hero.title")}
            </h1>

            <p className="mt-4 text-base md:text-lg leading-relaxed text-slate-600 max-w-xl">
              {t("hero.tagline")}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href="mailto:marleyrozo@gmail.com"
                className="inline-flex items-center justify-center rounded-xl bg-[#3f5232] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#2f3b25] hover:shadow"
              >
                {t("hero.cta", "Contáctame")}
              </a>

              <a
                href="https://github.com/yerikamarley"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-xl border border-[#3f5232]/25 bg-white px-5 py-2.5 text-sm font-medium text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-[#3f5232]/45 hover:bg-[#f4f6f1] hover:shadow"
              >
                GitHub
              </a>

              <a
                href="https://www.linkedin.com/in/yerika-marley-basto-rozo-605354150/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-xl border border-[#3f5232]/25 bg-white px-5 py-2.5 text-sm font-medium text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-[#3f5232]/45 hover:bg-[#f4f6f1] hover:shadow"
              >
                LinkedIn
              </a>
            </div>

            {/* Info en tarjeta (borde oliva suave) */}
            <div className="mt-7 rounded-2xl border border-[#3f5232]/15 bg-white/70 p-5 text-sm text-slate-700 backdrop-blur">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-[#3f5232]/70">
                    Email
                  </div>
                  <div className="mt-0.5 font-medium text-slate-800">
                    marleyrozo@gmail.com
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wide text-[#3f5232]/70">
                    Teléfono
                  </div>
                  <div className="mt-0.5 font-medium text-slate-800">
                    +57 314 203 5583
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <div className="text-xs uppercase tracking-wide text-[#3f5232]/70">
                    Ubicación
                  </div>
                  <div className="mt-0.5 font-medium text-slate-800">
                    Cúcuta, N. de Santander — Colombia
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Foto (aro premium oliva/gris) */}
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5 flex justify-center md:justify-end"
          >
            <div className="relative">
              <div className="absolute -inset-6 rounded-full bg-gradient-to-br from-[#4b5d3a]/18 to-slate-400/18 blur-2xl" />

              <div className="relative w-44 h-44 md:w-56 md:h-56 rounded-full p-[3px] bg-gradient-to-br from-[#3f5232]/55 to-slate-300/55">
                <div className="w-full h-full rounded-full overflow-hidden bg-white border border-white shadow-sm">
                  <img
                    src="https://i.postimg.cc/25fbm1kp/Whats-App-Image-2026-01-23-at-10-30-04-PM.jpg"
                    alt="Yerika Marley - foto profesional"
                    className="object-cover w-full h-full"
                  />
                </div>
              </div>

              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-[#3f5232]/20 bg-white/80 px-4 py-2 text-xs text-slate-700 backdrop-blur">
                <span className="text-[#3f5232]">●</span>{" "}
                {t("hero.badge", "Disponible para proyectos")}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
